import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  CheckboxInput,
  ErrorMessage,
  LoadingOrError,
  SubmitButton,
} from '@retainerclub/common';
import { UPDATE_CONTACT_SETTINGS } from '@retainerclub/shared-api';
import {
  MARKETING_LABEL,
  MarketingDescription,
  SMS_LABEL,
  SmsDescription,
} from '../../AccountConsent/ConsentCopy';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { initialValues, schema } from './helpers';

function CommunicationPreferencesForm({ user }) {
  const [mutationErrors, setMutationErrors] = useState(null);

  const [update, { error, loading }] = useMutation(UPDATE_CONTACT_SETTINGS);

  const memoizedInitialValues = useMemo(() => initialValues({ user }), [user]);

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, isValid, isLoading },
    formState,
    control,
    getValues,
    setValue,
    ...rest
  } = useForm({
    resolver: yupResolver(schema()),
    defaultValues: memoizedInitialValues,
    values: memoizedInitialValues,
    mode: 'onBlur',
  });

  async function submitRecord(values) {
    try {
      const smsAt = values.acceptSms ? new Date().toISOString() : null;
      const marketingAt = values.acceptMarketing
        ? new Date().toISOString()
        : null;
      const vals = {
        ...values,
        acceptSmsAt: smsAt,
        acceptMarketingAt: marketingAt,
      };
      const params = {
        variables: {
          input: {
            input: { ...vals },
            clientMutationId: crypto.randomUUID(),
          },
        },
      };

      const { data } = await update(params);
      const mErrors = data.updateContactSettings.errorMessages;

      if (mErrors.length > 0) {
        setMutationErrors(mErrors);
      } else {
        setMutationErrors(null);
      }
    } catch (err) {
      console.error(err);
    }
  }

  const shouldRender = LoadingOrError({
    loading,
    error,
    data: user,
    title: 'Communication Preferences',
  });

  if (shouldRender === true) {
    return (
      <FormProvider
        {...rest}
        control={control}
        register={register}
        handleSubmit={handleSubmit}
        formState={formState}
        getValues={getValues}
        setValue={setValue}
      >
        <form
          onSubmit={handleSubmit(submitRecord)}
          className="mb-10"
          noValidate
        >
          <div className="mx-auto w-full max-w-full px-4 md:max-w-7xl">
            <div className="mt-6">
              <CheckboxInput
                name="acceptSms"
                label={SMS_LABEL}
                description={<SmsDescription />}
                className="mb-6"
              />
              <CheckboxInput
                name="acceptMarketing"
                label={MARKETING_LABEL}
                description={<MarketingDescription />}
                className="mb-6"
              />
            </div>
            <div className="my-8">
              {error && (
                <div className="mt-6 sm:col-span-6">
                  <ErrorMessage error={error} />
                </div>
              )}

              {mutationErrors && (
                <div className="mt-6 sm:col-span-6">
                  <ErrorMessage errorMessages={mutationErrors} />
                </div>
              )}

              <SubmitButton
                isLoading={isLoading}
                isSubmitting={isSubmitting}
                isValid={isValid}
                label="Save Preferences"
                notRounded
                full={false}
              />
            </div>
          </div>
        </form>
      </FormProvider>
    );
  }

  return shouldRender;
}

CommunicationPreferencesForm.propTypes = {
  user: PropTypes.object.isRequired,
};

export default CommunicationPreferencesForm;
